import { useState } from "react";
import { ChevronDown } from "lucide-react";
import GuaranteedRentClosedModal from "./GuaranteedRentClosedModal";

export default function GuaranteedRentFAQ({ city }) {
  const [openIndex, setOpenIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const faqs = [
    {
      q: "What is guaranteed rent?",
      a: "Guaranteed rent (also called rent-to-rent or company let) is where we take a long-term lease on your property, pay you a fixed monthly income, and operate it as serviced accommodation. You stop being a landlord in the day-to-day sense — no guest calls, no void periods, no arrears. We become your tenant, on a multi-year contract, and deal with everything.",
    },
    {
      q: `How much guaranteed rent can I receive${city.name ? " in " + city.name : ""}?`,
      a: city.faqAnswer,
    },
    {
      q: "What do you do with my property?",
      a: "We operate it as serviced accommodation — listing it on Airbnb and 16+ short-let platforms. We furnish it (if needed), photograph it professionally, and manage all guests. You don't deal with any of this; you simply receive your fixed monthly payment.",
    },
    {
      q: "What's the difference between guaranteed rent and Airbnb management?",
      a: "With guaranteed rent, you receive a fixed monthly income regardless of occupancy — maximum stability, zero involvement. With Airbnb management, you earn more (typically 40–80% above long-term rent) but income varies month to month based on occupancy and pricing. Both are fully managed by us — it's a choice between certainty and upside.",
    },
    {
      q: "Will you refurbish my property?",
      a: "Subject to your approval, we may carry out light refurbishments or improvements to bring the property up to serviced accommodation standard. Any works are agreed with you upfront and funded by us — not charged to you.",
    },
    {
      q: "What's included in the service?",
      a: "Everything: professional photography, multi-platform listing, guest communication, cleaning between every stay, maintenance and repairs, compliance certificates (gas safety, EICR, EPC), and property care. You receive monthly statements and have no day-to-day involvement.",
    },
    {
      q: "How long is the contract?",
      a: "Our standard guaranteed rent agreements are 2–3 years, giving you long-term security and allowing us to invest in furnishing, refurbishment, and listing optimisation. Flexible terms are available for the right properties.",
    },
    {
      q: "What condition does my property need to be in?",
      a: "Properties should have valid safety certificates and meet minimum EPC requirements (typically E or above). If improvements are needed, we'll discuss them during the property visit — and in some cases we can fund them ourselves subject to agreement.",
    },
    {
      q: "Can I still use my property?",
      a: "Yes — we can build owner usage periods into the agreement. Many landlords block out a few weeks per year while still receiving guaranteed rent for the remaining months. Let us know your requirements and we'll structure an agreement that works.",
    },
    {
      q: "How quickly can you start paying?",
      a: "Most properties go live within 2–3 weeks of instruction, once the assessment, any required works, and setup are complete. We'll give you a clear timeline from the outset.",
    },
  ];

  return (
    <section style={{ backgroundColor: "#fff", padding: "clamp(60px,7vw,96px) clamp(20px,5vw,80px)" }}>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5C7268", marginBottom: 12, marginTop: 0 }}>
            FAQs
          </p>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em", lineHeight: 1.15, color: "#112215", margin: "0 auto 14px", maxWidth: 640 }}>
            Common questions about guaranteed rent.
          </h2>
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          {faqs.map(({ q, a }, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={q}
                style={{
                  borderTop: "1px solid #E8E4D5",
                  padding: "22px 0",
                }}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? -1 : i)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 16,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    padding: 0,
                  }}
                >
                  <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 16, color: "#112215", lineHeight: 1.5 }}>
                    {q}
                  </span>
                  <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "#EDE9DC",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "transform 0.3s",
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}>
                    <ChevronDown size={16} color="#5C7268" />
                  </div>
                </button>
                {isOpen && (
                  <div style={{ marginTop: 14, paddingLeft: 8 }}>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: "#5C7268", lineHeight: 1.7, margin: 0 }}>
                      {a}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ borderTop: "1px solid #E8E4D5" }} />
        </div>

        <div style={{ textAlign: "center", marginTop: 48 }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: "#5C7268", marginBottom: 20, marginTop: 0 }}>
            Still have questions?
          </p>
          <button
            onClick={() => setShowModal(true)}
            style={{
              background: "#112215", color: "#fff", border: "none", borderRadius: 8,
              padding: "14px 32px", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600,
              cursor: "pointer", transition: "background 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#1A3A2A"}
            onMouseLeave={e => e.currentTarget.style.background = "#112215"}
          >
            Get in touch
          </button>
        </div>
      </div>

      {showModal && <GuaranteedRentClosedModal onClose={() => setShowModal(false)} />}
    </section>
  );
}